import { useState } from 'react';
import { api, type Role } from '../api';
import { Banner, Card, Empty, Loading, Modal, parseNairaToKobo, useAsync, when } from '../ui';
import { FloatMeter } from './Overview';

const LEDGER_LABEL: Record<string, string> = {
  credit_funding: 'Funding',
  hold: 'Hold placed',
  release_hold: 'Hold released',
  debit_payout: 'Payout',
  refund_to_indrive: 'Returned to inDrive',
};

/**
 * The float.
 *
 * This is inDrive's money, ring-fenced. Everything on this screen is derived
 * from the append-only ledger — the balance shown is the balance the ledger
 * reproduces, which is exactly what reconciliation asserts each night.
 */
export function Float({ role }: { role: Role }) {
  const [selected, setSelected] = useState<string | undefined>();
  const { data, error, loading, reload } = useAsync(() => api.float(selected), [selected]);
  const [dialog, setDialog] = useState<'fund' | 'threshold' | 'unfreeze' | null>(null);
  const [amount, setAmount] = useState('');
  const [reference, setReference] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ tone: 'ok' | 'danger'; text: string } | null>(null);

  if (loading) return <Loading label="Loading float" />;
  if (error) return <Banner tone="danger" title="Could not load the float">{error}</Banner>;
  if (!data || data.accounts.length === 0) {
    return <Card><Empty title="No float account" hint="A programme float must be configured before any payout can run." /></Card>;
  }

  const account = data.accounts.find((a) => a.id === data.selected) ?? data.accounts[0]!;
  const kobo = parseNairaToKobo(amount);

  async function act() {
    setBusy(true);
    try {
      if (dialog === 'fund' && kobo) {
        await api.fund(account.id!, kobo, reference.trim());
        setNote({ tone: 'ok', text: 'Float credited. Approved referrals waiting on funds have been re-queued.' });
      } else if (dialog === 'threshold' && kobo) {
        await api.setThreshold(account.id!, kobo);
        setNote({ tone: 'ok', text: 'Alert threshold updated.' });
      } else if (dialog === 'unfreeze') {
        await api.unfreeze(account.id!, reference.trim());
        setNote({ tone: 'ok', text: 'Float unfrozen. Payouts will resume.' });
      }
      setDialog(null); setAmount(''); setReference('');
      reload();
    } catch (e) {
      setNote({ tone: 'danger', text: (e as Error).message });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="stack">
      {note && <Banner tone={note.tone}>{note.text}</Banner>}
      {account.frozen && (
        <Banner
          tone="danger"
          title="This float is frozen"
          action={role === 'zeeh_admin' ? <button className="btn sm" onClick={() => setDialog('unfreeze')}>Unfreeze</button> : undefined}
        >
          {account.frozen_reason ?? 'Reconciliation mismatch'}. No payout will be initiated while this stands.
        </Banner>
      )}

      <div className="grid cols-2">
        <Card
          title={account.programme}
          action={
            <div className="row">
              {role === 'zeeh_admin' && <button className="btn sm primary" onClick={() => setDialog('fund')}>Record top-up</button>}
              {(role === 'zeeh_admin' || role === 'indrive_admin') && (
                <button className="btn sm" onClick={() => setDialog('threshold')}>Threshold</button>
              )}
            </div>
          }
        >
          <div className="row" style={{ alignItems: 'baseline', gap: 10, marginBottom: 14 }}>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 30, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
              {account.available_display}
            </div>
            <div className="small muted">available to pay</div>
          </div>
          <FloatMeter float={account} />
          <dl className="dl" style={{ marginTop: 18 }}>
            <dt>Total balance</dt><dd>{account.balance_display}</dd>
            <dt>Committed (holds)</dt><dd>{account.active_holds_display}</dd>
            <dt>Alert threshold</dt>
            <dd>{BigInt(account.low_balance_threshold) > 0n ? formatK(account.low_balance_threshold) : <span className="muted">not set</span>}</dd>
            <dt>Currency</dt><dd>{account.currency}</dd>
          </dl>
        </Card>

        <Card title="Where this number comes from">
          <p className="small" style={{ marginTop: 0, lineHeight: 1.7, color: 'var(--ink-2)' }}>
            Every line below is permanent. Nobody can edit one, delete one, or wipe the list — not an
            operator, not an engineer, not us. The balance is simply those lines added up, and we add them
            up again every night. If the two ever disagree, the account locks itself until someone works
            out why.
          </p>
          <div className="sep" />
          <dl className="dl">
            <dt>Funding</dt><dd className="small">inDrive sent money in</dd>
            <dt>Hold</dt><dd className="small">a payout has started — the money is spoken for, but still here</dd>
            <dt>Payout</dt><dd className="small">the money has actually left</dd>
            <dt>Release</dt><dd className="small">the payout failed, so the money is free again</dd>
          </dl>
        </Card>
      </div>

      {data.accounts.length > 1 && (
        <div className="toolbar">
          {data.accounts.map((a) => (
            <button
              key={a.id}
              className={`btn sm${a.id === account.id ? ' primary' : ''}`}
              onClick={() => setSelected(a.id)}
            >
              {a.programme}
            </button>
          ))}
        </div>
      )}

      <Card title="Ledger" flush>
        {data.ledger.length === 0 ? (
          <Empty title="No movements yet" />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Entry</th><th>Movement</th><th>Narrative</th>
                  <th className="num">Amount</th><th className="num">Balance after</th><th className="num">When</th>
                </tr>
              </thead>
              <tbody>
                {data.ledger.map((l) => (
                  <tr key={l.id}>
                    <td className="mono small muted">{l.id}</td>
                    <td><span className={`ledger-type ${l.type}`}>{LEDGER_LABEL[l.type] ?? l.type}</span></td>
                    <td className="small">
                      {l.narrative}
                      {l.reference && <div className="muted mono" style={{ fontSize: 10.5 }}>{l.reference}</div>}
                    </td>
                    <td className="num">
                      {l.type === 'debit_payout' || l.type === 'refund_to_indrive' ? '−' : l.type === 'credit_funding' ? '+' : ''}
                      {l.amount_display}
                    </td>
                    <td className="num">{l.balance_after_display}</td>
                    <td className="num small muted">{when(l.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {dialog && (
        <Modal
          title={dialog === 'fund' ? 'Record a float top-up' : dialog === 'threshold' ? 'Low-balance alert threshold' : 'Unfreeze this float'}
          description={
            dialog === 'fund' ? "This credits inDrive's float. The reference must match the incoming bank credit — reconciliation will check it."
            : dialog === 'threshold' ? 'An alert is raised when available funds fall to or below this figure.'
            : 'Payouts will resume immediately. Only unfreeze once the mismatch has been explained.'
          }
          onClose={() => { setDialog(null); setAmount(''); setReference(''); }}
          footer={
            <>
              <button className="btn" onClick={() => { setDialog(null); setAmount(''); setReference(''); }}>Cancel</button>
              <button
                className="btn primary"
                disabled={busy || (dialog !== 'unfreeze' ? !kobo : reference.trim().length < 10) || (dialog === 'fund' && reference.trim().length < 3)}
                onClick={act}
              >
                {busy ? <span className="spin" /> : dialog === 'fund' ? 'Credit float' : dialog === 'threshold' ? 'Save' : 'Unfreeze'}
              </button>
            </>
          }
        >
          {dialog !== 'unfreeze' && (
            <div className="field">
              <label htmlFor="amt">Amount (naira)</label>
              <input id="amt" type="text" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="2,000,000" />
              <span className="hint">
                {amount && !kobo ? <span style={{ color: 'var(--danger)' }}>Enter a whole naira amount, optionally with kobo.</span>
                  : kobo ? `${formatK(kobo)} — ${kobo} kobo` : 'Stored and moved as integer kobo.'}
              </span>
            </div>
          )}
          <div className="field">
            <label htmlFor="ref">{dialog === 'unfreeze' ? 'Reason' : dialog === 'fund' ? 'Bank credit reference' : 'Note'}</label>
            {dialog === 'unfreeze'
              ? <textarea id="ref" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="e.g. Mismatch traced to a settlement file that arrived twice; ledger confirmed correct." />
              : <input id="ref" type="text" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="INDRIVE-TOPUP-2026-004" />}
            {dialog === 'unfreeze' && <span className="hint">At least 10 characters. Recorded in the audit log.</span>}
          </div>
        </Modal>
      )}
    </div>
  );
}

function formatK(kobo: string): string {
  const d = kobo.padStart(3, '0');
  return `₦${d.slice(0, -2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}.${d.slice(-2)}`;
}
